import { useEffect, useState } from 'react';
import { fetchEntity, fetchTask } from '../api/client.js';
import type { EntityResponse, EntityStep } from '../api/client.js';
import { resolveEditWiring } from '../lib/flowchart.js';
import type { FieldProjection, StepWiring, WiringBlock, WiringResponse } from '../lib/flowchart.js';
import type { WorkItemSummary } from '../types/workflow.js';

/**
 * The answer relationships around the step a fix item's `_edits` locator names — the options, which
 * one is keyed correct, where each routes, the text of each routed-to step, and (for a step with no
 * options of its own) which answer sends a learner here and which question this leads into.
 *
 * ## Why this exists
 *
 * A fix item's review row shows a before/after of ONE field and never loads the lesson
 * (`PatchPreview.tsx`; `lib/workItems.ts` sends only `op: 'create'` to the richer lesson screen). So a
 * defect that lives in the RELATIONSHIP between the edited field and the rest of the lesson is
 * invisible by construction. Worked case `alts-e4551-f0-drafted-111`, a rewrite of lesson 4551's step
 * 6 stem: the new stem ends "the investors want part of that USD 1 million back", which states the
 * correct answer ("It's a clawback provision") outright; and that correct option routes to step 8,
 * unedited, whose text describes the OLD stem and a high-water mark — the other concept the lesson
 * exists to distinguish. Both facts are one fetch away and neither was on screen.
 *
 * ## Scope, and what is deliberately NOT here
 *
 * One step's relationships, not the lesson. Thirty steps and twenty options is a lot of screen for a
 * three-minute decision, and a view that shows everything emphasises nothing. A step with no options,
 * no inbound route and no forward question renders nothing at all — intended.
 *
 * ## Current AND proposed, never one silently standing for the other
 *
 * The surroundings are the LIVE document, labelled as such. The EDITED field is additionally
 * projected forward, because judging the relationship the edit *creates* is the whole point: on the
 * worked case the live stem and the live feedback agree perfectly, and a live-only block would
 * reassure the reviewer about the exact edit it was built to catch (cross-model review finding 1).
 * The projection uses the executor's own anchoring rule and REFUSES rather than guessing, so it never
 * shows a state the executor would not produce — see `projectField` in `lib/flowchart.ts`.
 *
 * It is a preview of THIS item only. Where the item belongs to an atomic group (all members apply or
 * none), the siblings are not projected and the block says so rather than letting the reviewer read
 * the preview as the state that will ship.
 */
export function StepWiringPane({ item }: { item: WorkItemSummary }) {
  // `suggestedPatch`, the SAME patch the diff above renders — see the comment at the `PatchPreview`
  // call site in `WorkQueueDashboard.tsx`. It is tempting to use `reviewerPatch ?? suggestedPatch`
  // because that is what the executor applies, and an earlier draft of this file did: it is wrong on
  // a cancelled row, where a plain approve CLEARS a stale `reviewerPatch` and ships the suggestion.
  // Previewing text that pressing Approve discards is worse than the `approved_with_edit` mismatch it
  // would fix, and that mismatch is pre-existing on the diff itself.
  const patch = item.suggestedPatch;
  const target = wiringTarget(item);
  // Cheap pre-check so a non-envelope patch costs no request at all. A document-scoped envelope still
  // renders (a one-line note saying it was not evaluated) but needs no document, so it skips the
  // fetch too.
  const addressesAStep = hasStepScopedEdit(patch);
  const hasEnvelope = Array.isArray(patch?._edits) && patch._edits.length > 0;

  const [state, setState] = useState<LoadState>({ status: 'idle' });

  const key = target ? documentKey(target) : null;
  useEffect(() => {
    if (!target || !addressesAStep) {
      setState({ status: 'idle' });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    void loadDocument(target)
      .then((document) => {
        if (!cancelled) {
          setState({ status: 'ready', document, key: documentKey(target) });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState({ status: 'error' });
        }
      });
    return () => {
      cancelled = true;
    };
    // `key` is the identity of the document; re-running on the item object would refetch every render.
  }, [key, addressesAStep]);

  const targetLabel = target?.kind === 'task' ? 'practice question' : 'lesson';

  // An envelope that addresses no step needs no document, but its absence must not be read as
  // "checked, nothing found" — `resolveEditWiring` returns the note that says which it is.
  if (hasEnvelope && !addressesAStep) {
    return <Blocks blocks={resolveEditWiring({}, patch, { answerGated: !!item.answerGate })} item={item} targetLabel={targetLabel} />;
  }
  // A step-scoped envelope on a target with no document to read (a group target, or an unparseable
  // id) is a visible refusal, not silence: the diff names a step and we cannot show it.
  if (hasEnvelope && addressesAStep && !target) {
    return (
      <WiringFrame>
        <p className="text-xs text-amber-700" data-testid="step-wiring-error">
          This edit names a step, but the item does not point at a lesson or practice question whose
          answer wiring can be loaded (target {item.targetType} {String(item.targetId)}).
        </p>
      </WiringFrame>
    );
  }

  if (state.status === 'idle') {
    return null;
  }
  // A ready state left over from a DIFFERENT target: the effect has not run yet for this render, so
  // showing `state.document` here would resolve one item's patch against another item's lesson for a
  // commit (cross-model review finding 9).
  if (state.status === 'loading' || (state.status === 'ready' && state.key !== key)) {
    return <WiringFrame><p className="text-xs text-slate-400">Loading the {targetLabel}…</p></WiringFrame>;
  }
  if (state.status !== 'ready') {
    // Never a silent blank: an invisible failure is indistinguishable from "this step has no answers",
    // which is the reviewer's cue that there is nothing here to check.
    return (
      <WiringFrame>
        <p className="text-xs text-amber-700" data-testid="step-wiring-error">
          Could not load the {targetLabel} this edit belongs to, so its answer wiring is not shown.
          The diff above is unaffected.
        </p>
      </WiringFrame>
    );
  }

  return <Blocks blocks={resolveEditWiring(state.document, patch, { answerGated: !!item.answerGate })} item={item} targetLabel={targetLabel} />;
}

function Blocks({
  blocks,
  item,
  targetLabel,
}: {
  blocks: WiringBlock[];
  item: WorkItemSummary;
  targetLabel: string;
}) {
  if (blocks.length === 0) {
    return null;
  }
  // An atomic group applies whole or not at all, so the state that will ship is this item's edit
  // PLUS its siblings' — which this block does not model. 121 of the 344 real backlog items are in
  // multi-member groups, so calling the preview "the state that will ship" would be wrong more often
  // than not (cross-model review finding 2).
  // The DERIVED roster (`memberWorkItemIds`), which is what the platform actually knows; the
  // producer's `member_count` is declared over its full corpus and can be larger. Undercounting here
  // is the safe direction for a caveat — it never claims fewer siblings exist than we can name.
  const siblings = (item.atomicGroup?.memberWorkItemIds?.length ?? 0) - 1;
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-xs font-bold uppercase text-slate-400">Answer wiring · live {targetLabel}</h4>
      {blocks.map((block, index) =>
        block.kind === 'wiring' ? (
          <WiringCard key={`wiring-${block.wiring.stepId}-${index}`} wiring={block.wiring} />
        ) : block.kind === 'unresolved' ? (
          <UnresolvedCard block={block} key={`unresolved-${index}`} />
        ) : (
          <p className="text-[11px] leading-relaxed text-slate-500" data-testid="step-wiring-note" key={`note-${index}`}>
            {block.message}
          </p>
        ),
      )}
      {siblings > 0 ? (
        <p className="text-[11px] leading-relaxed text-amber-700" data-testid="step-wiring-group-caveat">
          This item is one of {siblings + 1} in an all-or-nothing group. The preview below applies this
          item&rsquo;s edit only — the other {siblings} {siblings === 1 ? 'edit is' : 'edits are'} not shown.
        </p>
      ) : null}
    </div>
  );
}

function WiringFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <h4 className="text-xs font-bold uppercase text-slate-400">Answer wiring</h4>
      {children}
    </div>
  );
}

function WiringCard({ wiring }: { wiring: StepWiring }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="step-wiring">
      <div className="flex flex-wrap items-center gap-2 pb-2">
        <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
          Step {wiring.stepId}
        </span>
        {wiring.editedFields.length > 0 ? (
          <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-blue-700">
            This item edits {wiring.editedFields.join(' and ')}
          </span>
        ) : null}
        {/*
          The response format is named because "keyed correct" means different things across them: a
          `numeric` step's responses are accepted values, not clickable options (cross-model review
          finding 9). Naming the format is cheap and stops the list overclaiming.
        */}
        <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-600">
          {wiring.responseFormat}
        </span>
        {wiring.isTryAgain ? (
          <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-600">
            Try-again — an incorrect answer returns here
          </span>
        ) : null}
      </div>

      <StepText wiring={wiring} />
      {/* The same warning can repeat (one per math block), so the text alone is not a unique key. */}
      {wiring.warnings.map((warning, index) => (
        <p className="pb-1 text-[11px] text-amber-700" key={`${index}:${warning}`}>{warning}</p>
      ))}

      {wiring.responses.length > 0 ? (
        <ul className="flex flex-col gap-1.5">
          {wiring.responses.map((response) => (
            <ResponseRow key={response.responseId} response={response} />
          ))}
        </ul>
      ) : null}

      {wiring.inbound.length > 0 ? (
        <div className="pt-2" data-testid="step-wiring-inbound">
          <p className="pb-1 text-[10px] font-bold uppercase tracking-wide text-slate-500">
            Learners reach this step by answering
          </p>
          <ul className="flex flex-col gap-1">
            {wiring.inbound.map((route) => (
              <li
                className="rounded-lg border border-slate-200 bg-white p-2 text-[11px]"
                data-testid={`step-wiring-inbound-${route.responseId}`}
                key={`${route.fromStepId}-${route.responseId}`}
              >
                <span className={correctnessClass(route.isCorrect)}>{correctnessLabel(route.isCorrect)}</span>
                <span className="pl-1.5 text-slate-800">{route.responseText}</span>
                <div className="pt-0.5 text-slate-500">
                  on step {route.fromStepId}: <span className="italic">{route.fromStepText}</span>
                </div>
              </li>
            ))}
          </ul>
          {wiring.inboundTotal > wiring.inbound.length ? (
            <p className="pt-1 text-[11px] text-slate-500">
              Showing {wiring.inbound.length} of {wiring.inboundTotal} routes into this step.
            </p>
          ) : null}
        </div>
      ) : null}

      {wiring.forward ? (
        <div className="pt-2" data-testid="step-wiring-forward">
          <p className="pb-1 text-[10px] font-bold uppercase tracking-wide text-slate-500">
            This step leads into step {wiring.forward.stepId}, a question
          </p>
          <p className="pb-1 text-[11px] italic text-slate-600">{wiring.forward.text}</p>
          <ul className="flex flex-col gap-1">
            {wiring.forward.responses.map((response) => (
              <li
                className="rounded-lg border border-slate-200 bg-white p-2 text-[11px]"
                data-testid={`step-wiring-forward-${response.responseId}`}
                key={response.responseId}
              >
                <span className={correctnessClass(response.isCorrect)}>{correctnessLabel(response.isCorrect)}</span>
                <span className="pl-1.5 text-slate-800">{response.text}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The step's own text: live, plus what it becomes if this item executes.
 *
 * Both are shown when they differ, and the projected one is labelled — a reviewer judging whether a
 * stem gives away its key has to read the stem the edit produces, not the one it replaces.
 */
function StepText({ wiring }: { wiring: StepWiring }) {
  return (
    <div className="pb-2">
      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">As it is live now</p>
      <p className="text-xs leading-relaxed text-slate-600">{wiring.stepText}</p>
      {wiring.stepValue ? (
        <p className="pt-1 text-xs leading-relaxed text-slate-500">
          <span className="font-semibold">value:</span> {wiring.stepValue}
        </p>
      ) : null}
      {/* One paragraph per edited field — an envelope may legally change both `prompt` and `value`. */}
      {Object.entries(wiring.projectedFields).map(([field, projection]) => (
        <Projection field={field} key={field} projection={projection} />
      ))}
    </div>
  );
}

function Projection({ field, projection }: { field: string; projection: FieldProjection }) {
  if (projection.state === 'unavailable') {
    return (
      <p className="pt-1 text-[11px] text-amber-700" data-testid="step-wiring-projection-unavailable">
        Cannot preview the edited {field}: {projection.reason}
      </p>
    );
  }
  if (projection.state === 'already_applied') {
    return (
      <p className="pt-1 text-[11px] text-slate-500" data-testid="step-wiring-projection">
        The live {field} above already matches this edit.
      </p>
    );
  }
  return (
    <div className="pt-1" data-testid="step-wiring-projection">
      <p className="text-[10px] font-bold uppercase tracking-wide text-blue-700">
        {field} with this item’s edit applied
      </p>
      <p className="text-xs leading-relaxed text-slate-900">{projection.value}</p>
    </div>
  );
}

function ResponseRow({ response }: { response: WiringResponse }) {
  const correct = response.isCorrect === true;
  return (
    <li
      className={`rounded-lg border p-2 ${correct ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white'}`}
      data-testid={`step-wiring-response-${response.responseId}`}
    >
      <div className="flex flex-wrap items-baseline gap-1.5">
        {/*
          Stated in WORDS, not signalled by a colour, and stated LITERALLY. "Distractor" is an
          academic claim; `is_correct: false` is a stored flag, and a Socratic branch can key every
          response true. `null` is a third state — the document does not say — and rendering it the
          same as `false` would assert something the content never claimed.
        */}
        {response.responseType === 'catchall' ? (
          <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-slate-500">
            Catchall — taken by input matching no option
          </span>
        ) : (
          <span className={correctnessClass(response.isCorrect)}>{correctnessLabel(response.isCorrect)}</span>
        )}
        {response.editedFields.length > 0 ? (
          <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-blue-700">
            This item edits {response.editedFields.join(' and ')}
          </span>
        ) : null}
        <span className="text-xs text-slate-800">{response.text}</span>
      </div>
      {Object.entries(response.projectedFields).map(([field, projection]) => (
        <Projection field={field} key={field} projection={projection} />
      ))}
      {response.feedback ? (
        <div className="pt-1 text-[11px] text-slate-500">
          <span className="font-semibold text-slate-600">Feedback: </span>
          <span className="italic">{response.feedback}</span>
        </div>
      ) : null}
      <div className="pt-1 text-[11px] text-slate-500">
        {response.targetStepId === null ? (
          // NOT "ends the question": a response with inline feedback and no route is the normal shape
          // for a task, and calling it an ending is a claim about the learner's path that the document
          // does not make (cross-model review finding 6).
          <span>No routed step (next_step_id is null).</span>
        ) : response.targetStepMissing ? (
          <span className="text-amber-700">
            Routes to step {response.targetStepId}, which is not in this document.
          </span>
        ) : (
          <>
            <span className="font-semibold text-slate-600">
              Step {response.targetStepId}
              {response.targetStepIsQuestion ? ' — another question — ' : ' '}
              then says:{' '}
            </span>
            <span className="italic">{response.targetStepText}</span>
          </>
        )}
      </div>
      {response.warnings.map((warning, index) => (
        <p className="pt-1 text-[11px] text-amber-700" key={`${index}:${warning}`}>{warning}</p>
      ))}
    </li>
  );
}

function correctnessLabel(isCorrect: boolean | null) {
  return isCorrect === true ? 'Keyed correct' : isCorrect === false ? 'Not keyed correct' : 'No answer key set';
}

function correctnessClass(isCorrect: boolean | null) {
  const base = 'rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide';
  if (isCorrect === true) return `${base} bg-emerald-600 text-white`;
  if (isCorrect === false) return `${base} bg-slate-200 text-slate-500`;
  return `${base} bg-amber-100 text-amber-800`;
}

function UnresolvedCard({ block }: { block: Extract<WiringBlock, { kind: 'unresolved' }> }) {
  return (
    <div
      className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800"
      data-testid="step-wiring-unresolved"
    >
      <p className="font-bold">Could not locate {block.label.split(' · ')[0]} in the live document</p>
      <p className="pt-1 leading-relaxed">{block.message}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading the live document
// ---------------------------------------------------------------------------

interface WiringTarget {
  kind: 'entity' | 'task';
  courseKey: string;
  id: number;
}

interface WiringDocument {
  steps?: EntityStep[];
  responses?: EntityResponse[];
}

type LoadState =
  | { status: 'idle' }
  | { status: 'loading' }
  /** The key is carried so a re-render that switched targets cannot show the previous lesson. */
  | { status: 'ready'; document: WiringDocument; key: string }
  | { status: 'error' };

/**
 * `targetId` is a STRING on the work item and a number in the document, and a group target has no
 * steps at all.
 *
 * The conversion mirrors the executor's (`release.service.ts` applies `Number(targetId)`) so the
 * block does not go quiet on an id the executor would happily act on. Anything that does not become a
 * finite number yields no target — and the caller renders a visible refusal for it rather than
 * nothing, because the diff still names a step.
 */
function wiringTarget(item: WorkItemSummary): WiringTarget | null {
  if (item.targetType !== 'entity' && item.targetType !== 'task') {
    return null;
  }
  if (!item.targetId || !item.courseKey) {
    return null;
  }
  const id = Number(item.targetId);
  if (!Number.isFinite(id)) {
    return null;
  }
  return { kind: item.targetType, courseKey: item.courseKey, id };
}

function documentKey(target: WiringTarget) {
  return `${target.courseKey}:${target.kind}:${target.id}`;
}

/** True when at least one `_edits` entry addresses a step or a response, i.e. could draw wiring. */
function hasStepScopedEdit(patch: Record<string, unknown>): boolean {
  const entries = patch?._edits;
  return (
    Array.isArray(entries)
    && entries.some((entry) => {
      const scope = (entry as { locator?: { scope?: unknown } } | null)?.locator?.scope;
      return scope === 'step' || scope === 'response';
    })
  );
}

/**
 * IN-FLIGHT REQUESTS ONLY — deliberately not a result cache.
 *
 * The block says the surroundings are "as it is live now", and one expanded row makes exactly one
 * decision about live content, so a cached document would let that sentence be false the moment a
 * wave executes or somebody saves in the editor. An earlier draft cached for five minutes on the
 * precedent of `lib/entityTitles.ts`; a title is not decision-bearing content and the precedent does
 * not carry (cross-model review finding 5). Deduplicating concurrent requests still costs nothing and
 * is what keeps a re-render or two rows on one lesson from issuing two fetches.
 */
const inFlight = new Map<string, Promise<WiringDocument>>();

function loadDocument(target: WiringTarget): Promise<WiringDocument> {
  const key = documentKey(target);
  const pending = inFlight.get(key);
  if (pending) {
    return pending;
  }
  let timer: ReturnType<typeof setTimeout> | undefined;
  const request = Promise.race([
    target.kind === 'task' ? fetchTask(target.courseKey, target.id) : fetchEntity(target.courseKey, target.id),
    new Promise<never>((_resolve, reject) => {
      timer = setTimeout(() => reject(new Error('timed out loading the document')), DOCUMENT_TIMEOUT_MS);
    }),
  ])
    .then((value) => value as WiringDocument)
    .finally(() => {
      if (timer !== undefined) {
        clearTimeout(timer);
      }
      inFlight.delete(key);
    });
  inFlight.set(key, request);
  return request;
}

/**
 * A request that never settles must not become permanent.
 *
 * `fetchEntity` / `fetchTask` take no abort signal, so without this a hung request stays in the map
 * forever and every later row for that lesson reuses it and spins (cross-model review finding 9). The
 * timeout rejects, which surfaces the visible "could not load" line, and `finally` evicts the entry so
 * the next expansion retries.
 */
const DOCUMENT_TIMEOUT_MS = 20_000;

/** Test-only. Production holds nothing beyond an in-flight request. */
export function resetWiringDocumentCache() {
  inFlight.clear();
}
